/* eslint-disable max-len */
import {
  useGetList,
  useLocaleState,
  useRefresh,
  useSidebarState,
  useTranslate
} from "react-admin";
import * as User from "providers/user";
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  Avatar,
  DialogActions,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  useMediaQuery,
  Theme,
  ListItemButton,
  Badge
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NotesIcon from "@mui/icons-material/Notes";
import Popover from "@mui/material/Popover";
import AppointmentServiceShow from "./AppointmentServiceShow";
import AppointmentServiceEdit from "./AppointmentServiceEdit";
import { AnimatedLoader } from "./AnimatedLoader";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  EmptyAppointmentIcon
} from "assets/Icons";
import dayjs, { Dayjs } from "dayjs";
import ArtistSingleCalendar from "./ArtistSingleCalendar";
import ArtistSingleCalendarDatesSelect from "./ArtistSingleCalendarDatesSelect";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { CustomButton } from "./CustomButton";
import { Link } from "react-router-dom";
import { useQuery } from "@apollo/client";
import CustomEmptyList from "./CustomEmptyList";
import { IServiceAddOn } from "types";
import { GET_APPOINTMENTS, GET_ARTISTS_WITH_AVAILABILITIES } from "../gql";
import { getZonedDate } from "utils/helpers";
import BookFromArtistCalendar from "./BookFromArtistCalendar";
import AddIcon from "@mui/icons-material/Add";
import BlockTimeFromArtistCalendar from "./BlockTimeFromArtistCalendar";
import DeleteIcon from "@mui/icons-material/Delete";
import { muiColors } from "themes/colors";
import ConfirmationModal from "./ConfirmationModal";
import * as Axios from "providers/axios";
import CancellationModal from "./CancellationModal";
import { TimeIcon } from "@mui/x-date-pickers";

const timeSlots = [
  "12AM",
  "",
  "",
  "",
  "1AM",
  "",
  "",
  "",
  "2AM",
  "",
  "",
  "",
  "3AM",
  "",
  "",
  "",
  "4AM",
  "",
  "",
  "",
  "5AM",
  "",
  "",
  "",
  "6AM",
  "",
  "",
  "",
  "7AM",
  "",
  "",
  "",
  "8AM",
  "",
  "",
  "",
  "9AM",
  "",
  "",
  "",
  "10AM",
  "",
  "",
  "",
  "11AM",
  "",
  "",
  "",
  "12PM",
  "",
  "",
  "",
  "1PM",
  "",
  "",
  "",
  "2PM",
  "",
  "",
  "",
  "3PM",
  "",
  "",
  "",
  "4PM",
  "",
  "",
  "",
  "5PM",
  "",
  "",
  "",
  "6PM",
  "",
  "",
  "",
  "7PM",
  "",
  "",
  "",
  "8PM",
  "",
  "",
  "",
  "9PM",
  "",
  "",
  "",
  "10PM",
  "",
  "",
  "",
  "11PM",
  "",
  "",
  ""
];

const LocationFilterInput = ({
  locationFilter,
  setLocationFilter,
  isSmall,
  clearBusinessHours
}: any) => {
  const { permissions } = User.useUser();
  const translate = useTranslate();
  const { data } = useGetList("Location", {
    filter: {
      company_id: permissions.companyId
    },
    pagination: { page: 1, perPage: 100 }
  });

  const handleChange = (event: SelectChangeEvent) => {
    localStorage.setItem("ServiceCalendarLocationFilter", event.target.value);
    setLocationFilter(event.target.value);
    if (event.target.value === "all") {
      clearBusinessHours();
    }
  };

  return (
    <FormControl
      sx={{
        minWidth: 120,
        maxWidth: isSmall ? 135 : 145,
        mt: "-6px",
        mb: "0px"
      }}
      size="small"
    >
      <InputLabel id="appointment-filter-select-id">
        {translate("common.locations")}
      </InputLabel>
      <Select
        labelId="appointment-filter-select-id"
        id="demo-select-small"
        value={locationFilter}
        label={translate("common.locations")}
        onChange={handleChange}
      >
        <MenuItem value="all">
          <em>{translate("common.all_locations")}</em>
        </MenuItem>
        {data?.map((location) => (
          <MenuItem key={location.id} value={location.id}>
            {location.nickname || location.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export interface IShowAppointmentServiceDialogData {
  id?: string;
  client?: string;
  time?: string;
  artist?: string;
  serviceName?: string;
  date?: string;
  artistPhoto?: string;
  clientPhoto?: string;
  guestName?: string;
  appointmentId?: string;
  appointmentNotes?: string;
  serviceDuration?: number;
  servicePrice?: number;
  locationName?: string;
  addOns?: IServiceAddOn[];
  isBlockedTime?: boolean;
}

export default function ArtistsCalendarList({
  selectedView,
  setSelectedView
}: {
  selectedView: string;
  setSelectedView: Dispatch<SetStateAction<string>>;
}) {
  const { user, permissions } = User.useUser();
  const { api } = Axios.useAxios();
  const refresh = useRefresh();
  const translate = useTranslate();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isSideBarOpen, _setIsSideBarOpen] = useSidebarState();
  const [localeState] = useLocaleState();
  const isSmall = useMediaQuery<Theme>("(max-width:968px)");
  const mobileBreakpoint = useMediaQuery<Theme>("(max-width:640px)");
  const isMedium = useMediaQuery<Theme>("(max-width:1024px)");
  const isIpadSizeScreen = isMedium && !mobileBreakpoint;
  const [showDialogData, setShowDialogData] =
    useState<IShowAppointmentServiceDialogData | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activeNote, setActiveNote] = useState<string>("");
  const [editDialogId, setEditDialogId] = useState<string>("");
  const [createDialogOpen, setCreateDialogOpen] = useState<string | null>(null);
  const [blockTimeDialogOpen, setBlockTimeDialogOpen] =
    useState<boolean>(false);
  const [selectedRange, setSelectedRange] = useState<any>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<any>(null);
  const [isPrev, setIsPrev] = useState<boolean>(false);
  const [forceLoading, setForceLoading] = useState<boolean>(true);
  const [blockTimeDeleteId, setBlockTimeDeleteId] = useState<
    string | undefined
  >("");
  const [cancelModalAppointmentId, setCancelModalAppointmentId] = useState<
    string | null
  >(null);
  const [businessHoursAnchorEl, setBusinessHoursAnchorEl] =
    useState<HTMLDivElement | null>(null);
  const [selectedBusinessHours, setSelectedBusinessHours] = useState<string>(
    localStorage.getItem("artistCalendarBusinessHours") || "hidden"
  );

  const dateFilterFromLocalStorage = null;
  const parsedDateFilter =
    dateFilterFromLocalStorage !== null && dateFilterFromLocalStorage !== "null"
      ? dayjs(JSON.parse(dateFilterFromLocalStorage))
      : null;
  const [calendarsDate, setCalendarsDate] = useState<Dayjs | null>(
    parsedDateFilter
  );
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<{
    title: string;
    dateForFilter: string;
  } | null>(null); // This state is crucial for date selection and filtering
  const [isNext, setIsNext] = useState<boolean>(false);
  const [locationFilter, setLocationFilter] = useState<any>(
    localStorage.getItem("ServiceCalendarLocationFilter") || "all"
  );
  const [dateAnchorEl, setDateAnchorEl] = useState<HTMLButtonElement | null>(
    null
  );

  const [sortedArtistIds, setSortedArtistIds] = useState<string[]>([]);
  const hasMountedSort = useRef(false);
  const lastDateFilterForSort = useRef<string | null | undefined>(undefined);

  const artistsResult = useQuery(GET_ARTISTS_WITH_AVAILABILITIES, {
    variables: {
      where: {
        user: {
          is: {
            joined_company_id: {
              equals: permissions?.companyId
            }
          }
        },
        is_active: {
          equals: true
        }
      },
      availabilitiesWhere2: {
        availability: {
          is: {
            start_time: {
              gte: selectedCalendarDate?.dateForFilter
                ? dayjs(selectedCalendarDate.dateForFilter)
                    .startOf("day")
                    .format()
                : dayjs().startOf("day").format(),
              lte: selectedCalendarDate?.dateForFilter
                ? dayjs(selectedCalendarDate.dateForFilter)
                    .endOf("day")
                    .format()
                : dayjs().endOf("day").format()
            }
          }
        }
      }
    },
    fetchPolicy: "no-cache"
  });

  const data = artistsResult?.data ?? artistsResult?.previousData;

  const locationIds = permissions.isArtist
    ? user?.joined_company?.locations?.map((location: any) => location.id)
    : permissions.impersonateLocationIds.length > 0
    ? permissions.impersonateLocationIds
    : user?.company?.locations?.map((location: any) => location.id);

  const appointmentsResult = useQuery(GET_APPOINTMENTS, {
    variables: {
      where: {
        AND: {
          start_time: {
            gte: selectedCalendarDate?.dateForFilter
              ? dayjs(selectedCalendarDate.dateForFilter)
                  .startOf("day")
                  .subtract(1, "day")
                  .format() // Fetch wider range
              : dayjs().startOf("day").format(),
            lte: selectedCalendarDate?.dateForFilter
              ? dayjs(selectedCalendarDate.dateForFilter)
                  .endOf("day")
                  .add(1, "day")
                  .format() // Fetch wider range
              : dayjs().endOf("day").format()
          },
          cancelled_at: {
            equals: null
          },
          location_id: {
            in: locationFilter === "all" ? locationIds : [locationFilter]
          }
        }
      }
    },
    fetchPolicy: "no-cache",
    pollInterval: 15000
  });

  const appointmentData =
    appointmentsResult?.data ?? appointmentsResult?.previousData;

  const allAppointmentServicesWithoutTimezone =
    appointmentData?.appointments?.reduce((acc: any, appointment: any) => {
      return [...acc, ...appointment.appointment_services];
    }, []);

  const allAppointmentServices = allAppointmentServicesWithoutTimezone?.filter(
    (item: any) => {
      if (!selectedCalendarDate?.dateForFilter) return false;
      const targetDay = dayjs(selectedCalendarDate.dateForFilter);
      const eventDay = dayjs(
        getZonedDate(item.start_time, item?.appointment?.location?.timezone)
      );
      return eventDay.isSame(targetDay, "day");
    }
  );

  const artistsWithAppointmentServices: any[] | undefined = useMemo(() => {
    if (!data?.artists) return undefined;
    return data.artists.map((artist: any) => {
      const matchingServices = allAppointmentServices?.filter(
        (service: any) => service.artist_id === artist.id
      );
      return {
        ...artist,
        appointment_services: matchingServices || []
      };
    });
  }, [data?.artists, allAppointmentServices]);

  const locations = permissions.impersonateCompanyData
    ? permissions.impersonateCompanyData.locations
    : [
        ...(user?.joined_company?.locations || []),
        ...(user?.company?.locations || [])
      ];
  const selectedLocation = locations?.find(
    (item: any) => item.id === locationFilter
  );

  useEffect(() => {
    const currentArtistsData = artistsWithAppointmentServices;
    const currentDateFilter = selectedCalendarDate?.dateForFilter;
    const currentLocationId = selectedLocation?.id;

    if (artistsResult.loading || appointmentsResult.loading) {
      return;
    }

    if (!currentArtistsData) {
      if (hasMountedSort.current && sortedArtistIds.length > 0) {
        setSortedArtistIds([]);
      }
      return;
    }

    if (
      !hasMountedSort.current ||
      lastDateFilterForSort.current !== currentDateFilter
    ) {
      const newSortedArtists = [...currentArtistsData].sort((a, b) => {
        const aServiceCount = a.appointment_services?.length || 0;
        const bServiceCount = b.appointment_services?.length || 0;

        if (bServiceCount !== aServiceCount) {
          return bServiceCount - aServiceCount;
        }

        if (selectedBusinessHours === "byAvailability") {
          let aRelevantAvailability = null;
          let bRelevantAvailability = null;

          const aAvailabilities = a?.availabilities;
          const bAvailabilities = b?.availabilities;

          if (currentLocationId) {
            aRelevantAvailability = aAvailabilities?.find(
              (item: any) =>
                item?.availability?.location_id === currentLocationId
            )?.availability;
            bRelevantAvailability = bAvailabilities?.find(
              (item: any) =>
                item?.availability?.location_id === currentLocationId
            )?.availability;
          } else {
            aRelevantAvailability = aAvailabilities?.[0]?.availability;
            bRelevantAvailability = bAvailabilities?.[0]?.availability;
          }

          const getAvailabilityDuration = (availability: any) => {
            if (!availability?.start_time || !availability?.end_time) return 0;

            const startTime = dayjs(availability.start_time);
            const endTime = dayjs(availability.end_time);
            return endTime.diff(startTime, "minutes");
          };

          const aDuration = getAvailabilityDuration(aRelevantAvailability);
          const bDuration = getAvailabilityDuration(bRelevantAvailability);

          const aHasAvailability = !!aRelevantAvailability;
          const bHasAvailability = !!bRelevantAvailability;

          if (aHasAvailability && !bHasAvailability) return -1;
          if (!aHasAvailability && bHasAvailability) return 1;

          if (aHasAvailability && bHasAvailability) {
            if (bDuration !== aDuration) {
              return bDuration - aDuration;
            }
          }
        }
        return 0;
      });

      setSortedArtistIds(newSortedArtists.map((artist) => artist.id));
      lastDateFilterForSort.current = currentDateFilter;
      if (!hasMountedSort.current) {
        hasMountedSort.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    artistsWithAppointmentServices,
    selectedCalendarDate?.dateForFilter,
    selectedLocation?.id,
    selectedBusinessHours,
    artistsResult.loading,
    appointmentsResult.loading
    // sortedArtistIds.length
  ]);

  const displayedArtists = useMemo(() => {
    if (!artistsWithAppointmentServices || sortedArtistIds.length === 0) {
      return [];
    }
    const artistsDataMap = new Map(
      artistsWithAppointmentServices.map((artist) => [artist.id, artist])
    );
    const result = sortedArtistIds
      .map((id) => artistsDataMap.get(id))
      .filter((artist): artist is any => !!artist);
    return result;
  }, [artistsWithAppointmentServices, sortedArtistIds]);

  const timezone =
    selectedLocation?.timezone || locations?.[0]?.timezone || "PST8PDT";

  useEffect(() => {
    localStorage.removeItem("ServiceCalendarDateFilter");
    const scrollTimer = setTimeout(() => {
      setForceLoading(false);
      scrollRef?.current?.scrollBy({
        top: 800
      });
    }, 500);
    return () => clearTimeout(scrollTimer);
  }, []);

  if (
    forceLoading ||
    (artistsResult?.loading &&
      !artistsResult.previousData &&
      displayedArtists.length === 0)
  ) {
    return <AnimatedLoader withTranslation />;
  }

  const handlePopoverOpen = (note: string) => {
    if (popoverRef.current) {
      setActiveNote(note);
      setAnchorEl(popoverRef.current);
    }
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setActiveNote("");
  };

  const open = Boolean(anchorEl);

  const closeShowDialog = () => {
    setShowDialogData(null);
  };
  const closeEditDialog = () => {
    setEditDialogId("");
    refresh();
  };

  const handleDatePopoverClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setDateAnchorEl(event.currentTarget);
  };

  const handleDatePopoverClose = () => {
    setDateAnchorEl(null);
  };
  const datePopoverOpen = Boolean(dateAnchorEl);
  const datePopoverId = datePopoverOpen ? "simple-date-popover" : undefined;

  const scrollLeft = () => {
    scrollRef?.current?.scrollBy({
      left: -500,
      behavior: "smooth"
    });
  };

  const scrollRight = () => {
    scrollRef?.current?.scrollBy({
      left: 500,
      behavior: "smooth"
    });
  };

  const handleBusinessHoursPopoverClick = (
    event: React.MouseEvent<HTMLDivElement>
  ) => {
    setBusinessHoursAnchorEl(event.currentTarget);
  };

  const handleBusinessHoursPopoverClose = () => {
    setBusinessHoursAnchorEl(null);
  };
  const businessHoursPopoverOpen = Boolean(businessHoursAnchorEl);
  const businessHoursPopoverId = businessHoursPopoverOpen
    ? "simple-businessHours-popover"
    : undefined;

  const handleSetBusinessHours = (controlName: string): void => {
    setSelectedBusinessHours(controlName);
    localStorage.setItem("artistCalendarBusinessHours", controlName);
    handleBusinessHoursPopoverClose();
  };
  const menuSizePlusEmptySpaces = isSideBarOpen ? 322 : 118;
  const columnWidth = 214;

  const currentDisplayableArtistCount = displayedArtists.filter((artist) =>
    permissions.isArtist ? artist?.id === user.artist.id : true
  ).length;

  const columnQuantityToFillScreen = Math.max(
    0,
    Math.floor((window.innerWidth - menuSizePlusEmptySpaces) / columnWidth)
  );

  const columnsForFillCount = permissions?.isArtist
    ? Math.max(0, columnQuantityToFillScreen - 1)
    : currentDisplayableArtistCount >= columnQuantityToFillScreen
    ? 0
    : Math.max(0, columnQuantityToFillScreen - currentDisplayableArtistCount);

  const businessHours = selectedLocation && [
    {
      daysOfWeek: [0],
      startTime: dayjs(selectedLocation?.sunday_hours_open)
        .tz(selectedLocation?.timezone)
        .format("HH:mm"),
      endTime: dayjs(selectedLocation?.sunday_hours_close)
        .tz(selectedLocation?.timezone)
        .format("HH:mm")
    },
    {
      daysOfWeek: [1],
      startTime: dayjs(selectedLocation?.monday_hours_open)
        .tz(selectedLocation?.timezone)
        .format("HH:mm"),
      endTime: dayjs(selectedLocation?.monday_hours_close)
        .tz(selectedLocation?.timezone)
        .format("HH:mm")
    },
    {
      daysOfWeek: [2],
      startTime: dayjs(selectedLocation?.tuesday_hours_open)
        .tz(selectedLocation?.timezone)
        .format("HH:mm"),
      endTime: dayjs(selectedLocation?.tuesday_hours_close)
        .tz(selectedLocation?.timezone)
        .format("HH:mm")
    },
    {
      daysOfWeek: [3],
      startTime: dayjs(selectedLocation?.wednesday_hours_open)
        .tz(selectedLocation?.timezone)
        .format("HH:mm"),
      endTime: dayjs(selectedLocation?.wednesday_hours_close)
        .tz(selectedLocation?.timezone)
        .format("HH:mm")
    },
    {
      daysOfWeek: [4],
      startTime: dayjs(selectedLocation?.thursday_hours_open)
        .tz(selectedLocation?.timezone)
        .format("HH:mm"),
      endTime: dayjs(selectedLocation?.thursday_hours_close)
        .tz(selectedLocation?.timezone)
        .format("HH:mm")
    },
    {
      daysOfWeek: [5],
      startTime: dayjs(selectedLocation?.friday_hours_open)
        .tz(selectedLocation?.timezone)
        .format("HH:mm"),
      endTime: dayjs(selectedLocation?.friday_hours_close)
        .tz(selectedLocation?.timezone)
        .format("HH:mm")
    },
    {
      daysOfWeek: [6],
      startTime: dayjs(selectedLocation?.saturday_hours_open)
        .tz(selectedLocation?.timezone)
        .format("HH:mm"),
      endTime: dayjs(selectedLocation?.saturday_hours_close)
        .tz(selectedLocation?.timezone)
        .format("HH:mm")
    }
  ];

  const businessHoursByAvailability = (availabilities: any) => {
    const availability = availabilities?.find(
      (item: any) => item?.availability?.location_id === selectedLocation?.id
    )?.availability;

    const baseTz = selectedLocation?.timezone || timezone; // Ensure timezone exists

    const startTime = availability?.start_time
      ? dayjs(availability.start_time).tz(baseTz).format("HH:mm")
      : null;
    const endTime = availability?.end_time
      ? dayjs(availability.end_time).tz(baseTz).format("HH:mm")
      : null;

    return [0, 1, 2, 3, 4, 5, 6].map((day) => ({
      daysOfWeek: [day],
      startTime: startTime,
      endTime: endTime
    }));
  };

  const handleSelectRange = (arg: any, artistId: string) => {
    if (permissions?.isArtist) return;
    setSelectedRange({ start_time: arg.start, end_time: arg.end });
    setCreateDialogOpen(artistId);
  };
  const closeCreateDialog = () => {
    setCreateDialogOpen(null);
    setSelectedRange(null);
    setBlockTimeDialogOpen(false);
  };
  const closeCreateDialogAndRefresh = () => {
    setCreateDialogOpen(null);
    setSelectedRange(null);
    appointmentsResult?.refetch();
  };

  const deleteBlockTimeHandler = async (appointmentId: string) => {
    try {
      await api.delete(`/v1/artists/block-time/${appointmentId}`);
      setBlockTimeDeleteId("");
      setShowDialogData(null);
      appointmentsResult?.refetch();
    } catch (error) {
      setBlockTimeDeleteId("");
    }
  };

  const artistsToRender = permissions.isArtist
    ? displayedArtists.filter((artist) => artist.id === user.artist.id)
    : displayedArtists;

  const finalColumns = [
    ...artistsToRender,
    ...Array(columnsForFillCount).fill(undefined)
  ];

  return (
    <div className="relative fc-view-container">
      <Box
        sx={{
          "& .MuiBox-root": { padding: 0 },
          "& .MuiToolbar-root": { minHeight: "0px", height: "0px" },
          "& .RaList-content": {
            backgroundColor: "#F6F4F1 !important",
            padding: 0,
            boxShadow: "none"
          }
        }}
      >
        <div
          ref={popoverRef}
          onClick={() => !!activeNote && handlePopoverClose()}
          aria-hidden
        >
          <div
            ref={scrollRef}
            className={`${
              isSideBarOpen
                ? `scroll-container-sidebar-open ${
                    isIpadSizeScreen ? "scroll-container-medium" : ""
                  } `
                : "scroll-container-sidebar-closed"
            } scroll-container-no-grab ${
              user.impersonate_company_id ? "impersonate-calendar" : ""
            } custom-artists-calendar fc-scroller`}
          >
            <div
              className={`absolute ${
                user.impersonate_company_id ? "top-[-58px]" : "top-[-52px]"
              } left-[0px] font-['OptimaLTStd'] font-[600] text-[24px] text-mono-primaryTextColor hidden md:block`}
            >
              {translate("navigation.services_calendar")}
            </div>
            <div className="h-[40px] w-[40px] rounded-[20px] absolute top-0 bg-mono-primaryBgColor z-[51]">
              <IconButton className="h-[40px] w-[40px]" onClick={scrollLeft}>
                <ChevronLeftIcon />
              </IconButton>
            </div>
            <div className="h-[40px] w-[40px] rounded-[20px] absolute top-0 right-[20px] bg-mono-primaryBgColor z-[51]">
              <IconButton className="h-[40px] w-[40px]" onClick={scrollRight}>
                <ChevronRightIcon />
              </IconButton>
            </div>
            {!!data?.artists?.length && (
              <div
                className={`absolute ${
                  user.impersonate_company_id ? "top-[-58px]" : "top-[-52px]"
                } flex ${
                  isSmall ? "gap-[8px] right-[8px]" : "gap-[16px] right-[16px]"
                } z-50 cursor-default`}
              >
                {!isSmall && !permissions?.isArtist && (
                  <Button
                    onClick={() =>
                      setCreateDialogOpen(displayedArtists?.[0]?.id)
                    }
                    disabled={
                      displayedArtists.length === 0 && !permissions?.isArtist
                    } // Disable if no artists to book for
                    disableRipple
                    endIcon={<AddIcon />}
                    className="!flex !justify-center !items-center !bg-mono-white height-[40px] !rounded-[20px] !text-[14px] !text-[#000] !font-['Inter'] !px-[12px]"
                    style={{
                      boxShadow:
                        " 0px 1px 5px rgba(0, 0, 0, 0.12), 0px 2px 2px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.2)"
                    }}
                  >
                    {translate("artists.create")}
                  </Button>
                )}
                {isSmall && !permissions?.isArtist && (
                  <IconButton
                    onClick={() =>
                      setCreateDialogOpen(displayedArtists?.[0]?.id)
                    }
                    disabled={
                      displayedArtists.length === 0 && !permissions?.isArtist
                    }
                    sx={{
                      backgroundColor: "#ffffff",
                      color: "#000000",
                      width: "36px",
                      height: "36px",
                      borderRadius: "18px",
                      "@media screen and (max-width:430px)": {
                        left: "8px"
                      }
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                )}
                {selectedLocation?.id && (
                  <Badge
                    color="error"
                    badgeContent={selectedBusinessHours !== "hidden" ? 1 : 0}
                    anchorOrigin={{ vertical: "top", horizontal: "left" }}
                    sx={{ "& .MuiBadge-badge": { top: "6px", left: "6px" } }}
                  >
                    <div
                      onClick={(e) => handleBusinessHoursPopoverClick(e)}
                      aria-hidden
                    >
                      <IconButton sx={{ width: 38, height: 38 }}>
                        <TimeIcon />
                      </IconButton>
                    </div>
                  </Badge>
                )}
                <Box sx={{ maxHeight: "38px" }}>
                  <LocationFilterInput
                    locationFilter={locationFilter}
                    setLocationFilter={setLocationFilter}
                    clearBusinessHours={() => {
                      setSelectedBusinessHours("hidden");
                      localStorage.removeItem("artistCalendarBusinessHours");
                    }}
                    isSmall={isSmall}
                  />
                </Box>
                <div className="flex bg-[#FAF9F7] border border-[rgba(48,47,46,0.23)] rounded-[32px]">
                  <IconButton
                    sx={{
                      borderRadius: "0px",
                      borderTopLeftRadius: "32px",
                      borderBottomLeftRadius: "32px"
                    }}
                    className="border-none p-[0px] w-[36px] h-[36px] font-['Inter'] font-[500] text-[14px] text-mono-primaryTextColor"
                    onClick={() => {
                      setIsPrev(true);
                      setTimeout(() => {
                        setIsPrev(false);
                      }, 0);
                      // This should ideally also call setCalendarsDate if ArtistsCalendarList controls the date
                      // For now, assuming ArtistSingleCalendar handles its date changes via setSelectedCalendarDate
                    }}
                  >
                    {" "}
                    <ArrowLeftIcon />{" "}
                  </IconButton>
                  <button
                    className={`border-none h-[36px] flex justify-center items-center font-['Inter'] font-[500] ${
                      isSmall ? "px-[2px] text-[12px]" : "px-[12px] text-[14px]"
                    } text-mono-primaryTextColor hover:bg-[rgba(0,0,0,0.04)]`}
                    onClick={handleDatePopoverClick}
                  >
                    {selectedCalendarDate?.dateForFilter
                      ? dayjs(selectedCalendarDate.dateForFilter).format(
                          "ddd, DD MMM"
                        ) // Ensure formatting is consistent
                      : selectedCalendarDate?.title || "Select Date"}
                  </button>
                  <IconButton
                    sx={{
                      borderRadius: "0px",
                      borderTopRightRadius: "32px",
                      borderBottomRightRadius: "32px"
                    }}
                    className="border-none p-[0px] w-[36px] h-[36px] font-['Inter'] font-[500] text-[14px] text-mono-primaryTextColor"
                    onClick={() => {
                      setIsNext(true);
                      setTimeout(() => {
                        setIsNext(false);
                      }, 0);
                      // Similarly, this should ideally update calendarsDate
                    }}
                  >
                    <ArrowRightIcon />
                  </IconButton>
                </div>
              </div>
            )}
            <div className="w-max h-max">
              <div className="flex sticky top-0 bg-mono-primaryBgColor z-50">
                <div className="min-w-[41px]" />
                {finalColumns?.map((artist: any, index: number) => (
                  <div
                    key={artist?.id || `header-empty-${index}`}
                    className="min-w-[214px] bg-mono-primaryBgColor flex justify-center pb-2 border-b border-[#ddd]"
                  >
                    <div className="flex items-center gap-2">
                      <div className="h-[48px] w-[48px] appearAnimation">
                        {artist?.user?.first_name && (
                          <Avatar
                            sx={{
                              bgcolor: "rgba(238, 234, 228, 0.9)",
                              color: "rgba(48, 47, 46, 0.95)",
                              width: "40px",
                              height: "40px",
                              fontFamily: "Inter",
                              fontSize: "18px"
                            }}
                            className="appearAnimation"
                            alt={artist?.user?.first_name || "img"}
                            src={
                              artist?.cover_photo_url ||
                              artist?.user?.photo_url ||
                              "monogram"
                            }
                          />
                        )}
                      </div>
                      <p className="max-w-[150px] truncate text-[16px] font-[400] text-mono-primaryTextColor font-['Inter'] capitalize">
                        {artist?.user?.first_name}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex">
                <div
                  className="flex flex-col sticky left-0 z-50"
                  style={{ marginTop: "-6px", marginRight: "-2px" }}
                >
                  {!!data?.artists?.length &&
                    timeSlots?.map((time, index) => (
                      <div
                        key={index}
                        style={{
                          height: "28px",
                          width: "41px",
                          backgroundColor: "#F6F4F1",
                          fontSize: "12px"
                        }}
                      >
                        {time}
                      </div>
                    ))}
                </div>
                {data &&
                  !data?.artists?.length && ( // No artists in the company at all
                    <div className="w-[375px] h-[calc(100vh-266px)] flex justify-center items-center">
                      <CustomEmptyList
                        emptyIcon={<EmptyAppointmentIcon />}
                        emptyTitle={translate("empty_list.no_artists_yet")}
                        emptyListCreateSubtitle={translate(
                          "empty_list.do_you_want_to_add_one"
                        )}
                        createButtonText="Add artist"
                        hasCreate={true}
                        createResource="Artist"
                      />
                    </div>
                  )}
                {/* Show this if artists exist but none are displayed (e.g. due to filters or if `displayedArtists` is empty) */}
                {data?.artists?.length > 0 &&
                  finalColumns.every((col) => col === undefined) &&
                  !permissions.isArtist && (
                    <div className="w-full h-[calc(100vh-266px)] flex justify-center items-center text-center text-gray-500">
                      <p>
                        {translate("empty_list.no_artists_for_filter", {
                          _: "No artists match the current criteria or date."
                        })}
                      </p>
                    </div>
                  )}
                <div className="min-w-max flex max-h-[2692px] border-b border-[#DDD] mb-6 overflow-y-hidden">
                  {finalColumns.map((artist: any, index: number) => (
                    <div
                      key={artist?.id || `calendar-column-empty-${index}`}
                      style={{
                        backgroundColor:
                          artist?.id && // Only apply style if it's a real artist
                          selectedLocation?.id &&
                          selectedBusinessHours === "byAvailability" &&
                          !artist?.availabilities?.find(
                            (item: any) =>
                              item?.availability?.location_id ===
                              selectedLocation?.id
                          )
                            ? "rgba(215, 215, 215, 0.3)"
                            : ""
                      }}
                      className="w-[214px] flex flex-col"
                    >
                      <ArtistSingleCalendar
                        locale={localeState}
                        select={handleSelectRange}
                        editable={true}
                        selectable={permissions?.isArtist ? false : true}
                        anchorEl={anchorEl}
                        setShowDialogData={setShowDialogData}
                        isPopoverOpen={open}
                        handlePopoverOpen={handlePopoverOpen}
                        handlePopoverClose={handlePopoverClose}
                        type={"appointmentService"}
                        artistId={artist?.id || `non-existing-artist-${index}`} // Pass ID if artist exists
                        isPrev={isPrev}
                        calendarsDate={calendarsDate} // Main date from parent
                        setSelectedCalendarDate={setSelectedCalendarDate} // Crucial for date changes from calendar
                        setCalendarsDate={setCalendarsDate} // Also allow calendar to update parent's Dayjs date
                        isNext={isNext}
                        index={index}
                        appointmentServicesData={artist?.appointment_services} // Fresh data from displayedArtists
                        businessHours={
                          artist?.id && selectedLocation?.id
                            ? selectedBusinessHours === "byLocation"
                              ? businessHours
                              : selectedBusinessHours === "byAvailability"
                              ? businessHoursByAvailability(
                                  artist?.availabilities
                                )
                              : undefined
                            : undefined
                        }
                        refetch={appointmentsResult?.refetch}
                        refresh={refresh}
                        timezone={timezone}
                      />
                      {createDialogOpen === artist?.id &&
                        artist?.id &&
                        !blockTimeDialogOpen && (
                          <BookFromArtistCalendar
                            createDialogOpen={createDialogOpen}
                            artist={artist}
                            selectedStart={
                              selectedRange?.start_time?.toISOString() ||
                              selectedCalendarDate?.dateForFilter
                            }
                            selectedEnd={selectedRange?.end_time?.toISOString()}
                            closeCreateDialogAndRefresh={
                              closeCreateDialogAndRefresh
                            }
                            filteredLocationId={locationFilter}
                            closeCreateDialog={closeCreateDialog}
                            setBlockTimeDialogOpen={setBlockTimeDialogOpen}
                          />
                        )}
                      {createDialogOpen === artist?.id &&
                        artist?.id &&
                        blockTimeDialogOpen && (
                          <BlockTimeFromArtistCalendar
                            createDialogOpen={createDialogOpen}
                            artist={artist}
                            selectedStart={
                              selectedRange?.start_time?.toISOString() ||
                              selectedCalendarDate?.dateForFilter
                            }
                            selectedEnd={selectedRange?.end_time?.toISOString()}
                            closeCreateDialogAndRefresh={
                              closeCreateDialogAndRefresh
                            }
                            filteredLocationId={locationFilter}
                            closeCreateDialog={closeCreateDialog}
                            setBlockTimeDialogOpen={setBlockTimeDialogOpen}
                          />
                        )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Dialogs and Popovers remain largely the same */}
        {!!showDialogData && (
          <Dialog
            open={!!showDialogData}
            onClose={closeShowDialog}
            sx={{
              "& .MuiDialogContent-root": { padding: "0px" },
              "& .MuiDialog-paper": {
                borderRadius: "8px",
                backgroundColor: "#fff",
                maxWidth: "520px",
                width: "100%"
              }
            }}
          >
            <DialogTitle sx={{ display: "flex", padding: "4px 16px 0px 16px" }}>
              <Button
                sx={{
                  minWidth: "unset",
                  whiteSpace: "nowrap",
                  fontSize: "12px"
                }}
                startIcon={<NotesIcon />}
                onClick={() => {
                  if (showDialogData?.appointmentId) {
                    setEditDialogId(showDialogData.appointmentId);
                  }
                }}
              >
                {" "}
                {translate("appointment.note")}{" "}
              </Button>
              <div className="w-full flex uppercase justify-center items-center ml-[-12px] text-[14px] font-['Inter'] font-[600]">
                {showDialogData?.isBlockedTime
                  ? `${translate("appointment.blocked_time")}`
                  : translate("appointment.appointment_title_short", {
                      _: "APPOINTMENT"
                    })}
              </div>
              <IconButton onClick={closeShowDialog}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <AppointmentServiceShow
                showDialogData={showDialogData}
                setShowDialogData={setShowDialogData}
                refresh={() => appointmentsResult?.refetch()}
              />
            </DialogContent>
            <DialogActions
              sx={{
                justifyContent: showDialogData?.isBlockedTime
                  ? "flex-end"
                  : "space-between"
              }}
            >
              {showDialogData?.isBlockedTime ? (
                <CustomButton
                  text={translate("common.delete")}
                  variant="secondary"
                  onClick={() =>
                    setBlockTimeDeleteId(showDialogData?.appointmentId)
                  }
                  sx={{
                    color: muiColors.error,
                    border: `solid 1px ${muiColors.error}`,
                    svg: {
                      fill: muiColors.error,
                      path: { fill: muiColors.error }
                    }
                  }}
                  startIcon={<DeleteIcon />}
                />
              ) : permissions?.isArtist ? (
                <div />
              ) : (
                <CustomButton
                  text={translate("appointment.cancel_full_appointment")}
                  variant="secondary"
                  onClick={() =>
                    setCancelModalAppointmentId(
                      showDialogData?.appointmentId || null
                    )
                  }
                  sx={{
                    color: muiColors.error,
                    border: `solid 1px ${muiColors.error}`,
                    svg: {
                      fill: muiColors.error,
                      path: { fill: muiColors.error }
                    }
                  }}
                />
              )}
              {!showDialogData?.isBlockedTime && (
                <Link
                  to={`/dashboard/Appointment/${showDialogData?.appointmentId}/show`}
                >
                  <CustomButton
                    variant="secondary"
                    text={translate("appointment.appointment_details")}
                  />
                </Link>
              )}
            </DialogActions>
            {blockTimeDeleteId && (
              <ConfirmationModal
                open={!!blockTimeDeleteId}
                handleConfirm={() => deleteBlockTimeHandler(blockTimeDeleteId)}
                onClose={() => setBlockTimeDeleteId("")}
                text="Are you sure you want to delete? This action can't be reversed, so please be certain before proceeding."
                buttonText="Delete"
              />
            )}
            {!blockTimeDeleteId && cancelModalAppointmentId && (
              <CancellationModal
                cancelModalAppointmentId={cancelModalAppointmentId}
                setCancelModalAppointmentId={setCancelModalAppointmentId}
                refreshCalendarAndCloseShowModal={() => {
                  appointmentsResult?.refetch();
                  closeShowDialog();
                }}
              />
            )}
          </Dialog>
        )}
        {!!editDialogId && (
          <Dialog
            open={!!editDialogId}
            onClose={() => setEditDialogId("")}
            sx={{
              "& .MuiDialogContent-root": { padding: "0px" },
              "& .MuiDialog-paper": {
                borderRadius: "8px",
                backgroundColor: "#fff",
                maxWidth: "410px",
                width: "100%"
              }
            }}
          >
            <DialogTitle sx={{ display: "flex", padding: "4px 16px 0px 16px" }}>
              <div className="w-full flex justify-center items-center -mr-[42px] text-[14px] font-['Inter'] font-[600]">
                {" "}
                {translate("appointment.note")}{" "}
              </div>
              <IconButton onClick={() => setEditDialogId("")}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <AppointmentServiceEdit
                editDialogId={editDialogId}
                closeEditDialog={closeEditDialog}
                setShowDialogData={setShowDialogData}
              />
            </DialogContent>
          </Dialog>
        )}
        <Popover
          id="mouse-over-popover"
          sx={{ pointerEvents: "none" }}
          open={open}
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: "center", horizontal: "center" }}
          transformOrigin={{ vertical: "center", horizontal: "center" }}
          onClose={handlePopoverClose}
          disableRestoreFocus
        >
          <div className="bg-mono-white p-[70px] max-w-[500px] text-[22px] font-['OptimaLTStd'] font-[500] text-mono-primaryTextColor">
            {activeNote}
          </div>
        </Popover>
        <Popover
          id={datePopoverId}
          open={datePopoverOpen}
          anchorEl={dateAnchorEl}
          onClose={handleDatePopoverClose}
          anchorOrigin={{ vertical: "center", horizontal: "center" }}
          transformOrigin={{ vertical: -25, horizontal: "center" }}
        >
          <ArtistSingleCalendarDatesSelect
            setCalendarsDate={setCalendarsDate} // This should update the Dayjs object
            // If ArtistSingleCalendarDatesSelect also needs to set the string version for selectedCalendarDate:
            // setSelectedCalendarDate={setSelectedCalendarDate} // Pass this if it's also responsible for the string version
            handleDatePopoverClose={handleDatePopoverClose}
            selectedView={selectedView}
            setSelectedView={setSelectedView}
          />
        </Popover>
        <Popover
          id={businessHoursPopoverId}
          open={businessHoursPopoverOpen}
          anchorEl={businessHoursAnchorEl}
          onClose={handleBusinessHoursPopoverClose}
          anchorOrigin={{ vertical: "center", horizontal: "center" }}
          transformOrigin={{ vertical: -25, horizontal: "center" }}
        >
          <div className="bg-[#fff] px-4 py-2 font-[700]">Business Hours:</div>
          <ListItemButton
            onClick={() => handleSetBusinessHours("hidden")}
            sx={{
              backgroundColor:
                selectedBusinessHours === "hidden"
                  ? "rgba(184, 114, 42, 0.08)"
                  : "#fff"
            }}
          >
            Hidden
          </ListItemButton>
          <ListItemButton
            onClick={() => handleSetBusinessHours("byLocation")}
            sx={{
              backgroundColor:
                selectedBusinessHours === "byLocation"
                  ? "rgba(184, 114, 42, 0.08)"
                  : "#fff"
            }}
          >
            Location open/close hours
          </ListItemButton>
          <ListItemButton
            onClick={() => {
              if (hasMountedSort?.current) {
                hasMountedSort.current = false;
              }
              handleSetBusinessHours("byAvailability");
            }}
            sx={{
              backgroundColor:
                selectedBusinessHours === "byAvailability"
                  ? "rgba(184, 114, 42, 0.08)"
                  : "#fff"
            }}
          >
            Artists availabilities
          </ListItemButton>
        </Popover>
      </Box>
    </div>
  );
}
